import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Box } from '@mui/material';
import BulkUploadActionsMenu from './BulkUploadActionsMenu';
import CustomPagination from '../../../components/customPagination';
import dayjs from 'dayjs';
import { useGetBulkUploadsHandler } from '../dataHandlers/getBulkUploadsHandler';
import { useGetBulkUploadTemplateConfigsHandler } from '../dataHandlers/getBulkUploadTemplateConfigsHandler';
import StatusChip from '../../../components/StatusChip';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';

const bulkUploadCategoryMapping = {
  NON_E_INVOICE: { label: 'Non-E-Invoice', color: 'default' },
  E_INVOICE: { label: 'E-Invoice', color: 'success' },
  CUSTOMER: { label: 'Customer', color: 'info' },
  SUPPLIER: { label: 'Supplier', color: 'warning' },
  PRODUCT: { label: 'Product', color: 'secondary' },
};

const BulkUploadTable = ({ handlePaginationChange, searchQuery, showCreatedBy = false }) => {
  const { category, subCategory } = searchQuery ?? {};
  const { bulkUploads, totalDocuments, isLoading } = useGetBulkUploadsHandler({ searchQuery });
  const { getTemplateDisplayName } = useGetBulkUploadTemplateConfigsHandler({
    category,
    subCategory,
    silent: true,
  });
  const { bulkUpload } = useTranslatedMessages();
  const t = bulkUpload?.table ?? {};
  const col = t.columns ?? {};
  const cat = t.category ?? {};

  const createdByColumn = {
    field: 'uploadedBy',
    headerName: col.created_by ?? 'Created By',
    flex: 1,
    renderCell: (params) => params.row.uploadedBy ?? '',
  };

  const columns = [
    {
      field: 'status',
      headerName: col.status ?? 'Status',
      flex: 0.8,
      id: 'bulk-upload-status',
      valueFormatter: (params) => {
        return params
          ?.split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      },
    },
    {
      field: 'category',
      headerName: col.category ?? 'Category',
      flex: 0.8,
      renderCell: (params) => {
        const base = bulkUploadCategoryMapping[params?.row?.category] || {};
        const translatedLabel =
          params?.row?.category === 'NON_E_INVOICE'
            ? (cat.non_e_invoice ?? base.label ?? '')
            : params?.row?.category === 'E_INVOICE'
              ? (cat.e_invoice ?? base.label ?? '')
              : params?.row?.category === 'CUSTOMER'
                ? (cat.customer ?? base.label ?? '')
                : params?.row?.category === 'SUPPLIER'
                  ? (cat.supplier ?? base.label ?? '')
                  : params?.row?.category === 'PRODUCT'
                    ? (cat.product ?? base.label ?? '')
                    : (base.label ?? '');
        const color = base.color ?? 'default';
        return <StatusChip color={color} label={translatedLabel} icon={'none'} />;
      },
    },
    {
      field: 'filename',
      headerName: col.file_name ?? 'File Name',
      flex: 1.5,
    },
    {
      field: 'source',
      headerName: col.source ?? 'Source',
      flex: 0.5,
      valueFormatter: (params) => {
        return params === 'BULK_UPLOAD' ? 'WEB' : params?.toUpperCase();
      },
    },
    {
      field: 'createdOn',
      headerName: col.upload_date ?? 'Upload Date',
      flex: 1,
      valueFormatter: (params) => {
        const timestamp = params;
        if (!timestamp) return '';
        return dayjs(timestamp).format('DD-MM-YYYY hh:mm:ss A');
      },
    },
    ...(showCreatedBy ? [createdByColumn] : []),
    {
      field: 'totalDocumentsCount',
      headerName: col.total ?? 'Total',
      flex: 0.4,
    },
    {
      field: 'processedDocumentsCount',
      headerName: col.imported_successfully ?? 'Imported Successfully',
      flex: 0.5,
    },
    {
      field: 'errorDocumentsCount',
      headerName: col.import_failed ?? 'Import Failed',
      flex: 0.4,
    },
    {
      field: 'templateName',
      headerName: col.template_name ?? 'Template Name',
      flex: 1,
      valueFormatter: (templateName) => getTemplateDisplayName(templateName),
    },
    {
      field: 'row',
      headerName: col.actions ?? 'Actions',
      flex: 0.5,
      sortable: false,
      renderCell: ({ row }) => <BulkUploadActionsMenu row={row} />,
    },
  ];

  return (
    <Box>
      <DataGrid
        rows={[...bulkUploads].sort((a, b) => {
          const dateA = new Date(a.created_on).toLocaleString();
          const dateB = new Date(b.created_on).toLocaleString();
          return new Date(dateB).getTime() - new Date(dateA).getTime();
        })}
        columns={columns}
        getRowId={(row) => row.id}
        paginationMode="server"
        rowCount={totalDocuments}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
        }}
        pageSizeOptions={[10, 25]}
        disableRowSelectionOnClick
        onPaginationModelChange={(model) => {
          handlePaginationChange(model.page, model.pageSize);
        }}
        slots={{
          pagination: CustomPagination,
        }}
      />
    </Box>
  );
};

export default BulkUploadTable;
